import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { isInstalledPWA } from '@/lib/notificationService';

interface SWUpdateState {
  needRefresh: boolean;
  isChecking: boolean;
  registration: ServiceWorkerRegistration | null;
}

// Check if we're on mobile browser (not installed PWA)
const isMobileBrowser = () => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  return isMobile && !isInstalledPWA();
};

export function useSWUpdate() {
  const [state, setState] = useState<SWUpdateState>({
    needRefresh: false,
    isChecking: false,
    registration: null,
  });
  const lastCheckRef = useRef<number>(0);

  useEffect(() => {
    // Nettoyer le flag de mise à jour si présent depuis plus de 15 secondes
    const updatingTimestamp = sessionStorage.getItem('sw_updating');
    if (updatingTimestamp) {
      const elapsed = Date.now() - parseInt(updatingTimestamp, 10);
      if (elapsed > 15000) {
        console.log('[SW Update] Cleaning stale update flag');
        sessionStorage.removeItem('sw_updating');
      }
    }
    
    // Vérifier si on vient juste de faire une mise à jour (dans les 10 dernières secondes)
    const lastUpdateTime = sessionStorage.getItem('sw_last_update');
    const justUpdated = lastUpdateTime && (Date.now() - parseInt(lastUpdateTime, 10) < 10000);
    if (justUpdated) {
      console.log('[SW Update] Just updated, clearing flag and skipping initial check');
      sessionStorage.removeItem('sw_last_update');
      // Ne pas montrer le bouton "Mettre à jour" immédiatement après une mise à jour
      return;
    }

    if (!('serviceWorker' in navigator)) {
      console.log('[SW Update] Service Worker not supported');
      return;
    }

    // On mobile browser, don't auto-update - just notify
    const shouldAutoReload = !isMobileBrowser();

    const registerSW = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          updateViaCache: 'none',
        });

        console.log('[SW Update] Service Worker registered:', registration);
        setState(prev => ({ ...prev, registration }));

        // Attendre un moment avant de vérifier s'il y a une mise à jour en attente
        // Cela évite de détecter le worker en cours de transition
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Vérifier s'il y a une mise à jour en attente
        const isUpdating = sessionStorage.getItem('sw_updating');
        if (registration.waiting && !isUpdating) {
          console.log('[SW Update] Update waiting on registration');
          // Only set needRefresh for installed PWAs
          if (!isMobileBrowser()) {
            setState(prev => ({ ...prev, needRefresh: true }));
          }
        }

        // Écouter les nouvelles mises à jour
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('[SW Update] New worker installing');

          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[SW Update] New version ready');
              
              // Ignorer si une mise à jour est déjà en cours
              const isUpdating = sessionStorage.getItem('sw_updating');
              if (isUpdating) {
                console.log('[SW Update] Update already in progress, skipping notification');
                return;
              }
              
              // Only trigger auto-update flow for installed PWAs
              if (!isMobileBrowser()) {
                setState(prev => ({ ...prev, needRefresh: true }));
                toast.success('Nouvelle version disponible !', {
                  description: 'L\'app va se mettre à jour automatiquement.',
                  duration: 3000,
                });
              } else {
                // For mobile browser, just skip waiting silently - next refresh will get new version
                console.log('[SW Update] Mobile browser - new version will apply on next visit');
              }
            }
          });
        });

        // Only do periodic checks for installed PWAs
        if (shouldAutoReload) {
          // Vérifier les mises à jour périodiquement (toutes les 60 secondes)
          const intervalId = setInterval(() => {
            registration.update().catch(console.error);
          }, 60 * 1000);

          // Vérifier au retour sur l'app (visibilitychange)
          const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
              const now = Date.now();
              // Ne vérifier que si la dernière vérification date de plus de 30 secondes
              if (now - lastCheckRef.current > 30000) {
                console.log('[SW Update] App became visible, checking for updates...');
                lastCheckRef.current = now;
                registration.update().catch(console.error);
              }
            }
          };

          document.addEventListener('visibilitychange', handleVisibilityChange);

          // Cleanup
          return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          };
        }

      } catch (error) {
        console.error('[SW Update] Registration failed:', error);
      }
    };

    // Écouter le message du controllerchange (quand un nouveau SW prend le contrôle)
    let refreshing = false;
    const handleControllerChange = () => {
      const isUpdating = sessionStorage.getItem('sw_updating');
      
      // Only auto-reload for installed PWAs, not mobile browsers
      // Et seulement si une mise à jour a été demandée par l'utilisateur
      if (!refreshing && shouldAutoReload && isUpdating) {
        refreshing = true;
        console.log('[SW Update] Controller changed, cleaning flag and reloading...');
        sessionStorage.removeItem('sw_updating');
        window.location.reload();
      } else if (!shouldAutoReload) {
        console.log('[SW Update] Mobile browser - skipping auto-reload');
      } else if (!isUpdating) {
        console.log('[SW Update] Controller changed but no update flag - skipping reload to prevent loop');
      }
    };
    
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    registerSW();
    
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  const checkForUpdates = useCallback(async (): Promise<boolean> => {
    if (!state.registration) {
      console.log('[SW Update] No registration available');
      return false;
    }

    setState(prev => ({ ...prev, isChecking: true }));

    try {
      // Ajouter un timeout de 10 secondes pour éviter le blocage
      const updatePromise = state.registration.update();
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 10000)
      );
      
      await Promise.race([updatePromise, timeoutPromise]);
      console.log('[SW Update] Update check completed');
      
      // Attendre un peu pour laisser le temps au SW de s'installer
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const hasUpdate = !!state.registration.waiting;
      setState(prev => ({ ...prev, isChecking: false, needRefresh: hasUpdate }));
      
      return hasUpdate;
    } catch (error) {
      console.error('[SW Update] Update check failed:', error);
      setState(prev => ({ ...prev, isChecking: false }));
      return false;
    }
  }, [state.registration]);

  const updateSW = useCallback(async (): Promise<boolean> => {
    const registration = state.registration;
    
    if (!registration?.waiting) {
      console.log('[SW Update] No waiting worker');
      return false;
    }

    console.log('[SW Update] Sending skip waiting message');
    
    // Utiliser MessageChannel pour une communication fiable
    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        console.log('[SW Update] Received response from SW:', event.data);
        resolve(true);
      };
      
      // Envoyer le message avec le port pour la réponse
      registration.waiting.postMessage({ type: 'SKIP_WAITING' }, [messageChannel.port2]);
      
      // Fallback si pas de réponse après 2 secondes
      setTimeout(() => {
        console.log('[SW Update] No response from SW, assuming success');
        resolve(true);
      }, 2000);
    });
  }, [state.registration]);

  return {
    needRefresh: state.needRefresh,
    isChecking: state.isChecking,
    checkForUpdates,
    updateSW,
  };
}
